import { api } from './client';
import type {
  Holding,
  WatchlistItem,
  PortfolioHeader,
  SystemHealth,
  LiveConsole,
  AgentSession,
  TimelineEvent,
  AgentLogDetail,
  Performance,
  PerformanceMode,
  Analytics,
  RealizedTradeRow,
  CostReport,
  RiskPosture,
  RiskConditionChip,
  ScannerDiscovery,
  TradeJournalRow,
  TradeJournalDetail,
  NewsArticle,
  Reflection,
  MarketIndices,
} from './types';

export const dashboardApi = {
  holdings: () => api<Holding[]>('/api/holdings'),
  watchlist: () => api<WatchlistItem[]>('/api/watchlist'),
  portfolioHeader: () => api<PortfolioHeader>('/api/portfolio/header'),
  systemHealth: () => api<SystemHealth>('/api/system/health'),

  liveConsole: () => api<LiveConsole>('/api/console/today'),

  agentSessions: (date?: string) =>
    api<AgentSession[]>(`/api/agents/sessions${date ? `?date=${date}` : ''}`),
  agentTimeline: (sessionId: string) =>
    api<TimelineEvent[]>(`/api/agents/sessions/${encodeURIComponent(sessionId)}/timeline`),
  agentLogDetail: (logId: string) =>
    api<AgentLogDetail>(`/api/agents/logs/${encodeURIComponent(logId)}`),

  performance: (mode: PerformanceMode, from?: string, to?: string) => {
    const params = new URLSearchParams({ mode });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return api<Performance>(`/api/performance/equity-curve?${params}`);
  },

  analyticsSummary: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return api<Analytics>(`/api/analytics/summary${qs ? `?${qs}` : ''}`);
  },
  analyticsRealized: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return api<RealizedTradeRow[]>(`/api/analytics/realized${qs ? `?${qs}` : ''}`);
  },

  costDaily: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return api<CostReport>(`/api/cost/daily${qs ? `?${qs}` : ''}`);
  },

  riskPosture: () => api<RiskPosture>('/api/risk/posture'),
  riskConditions: () => api<RiskConditionChip[]>('/api/risk/conditions'),

  scannerDiscovery: (date?: string) =>
    api<ScannerDiscovery>(`/api/scanners/discovery${date ? `?date=${date}` : ''}`),

  tradeJournal: (params: { from?: string; to?: string; side?: string; status?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.side) qs.set('side', params.side);
    if (params.status) qs.set('status', params.status);
    const s = qs.toString();
    return api<TradeJournalRow[]>(`/api/trades${s ? `?${s}` : ''}`);
  },
  tradeJournalDetail: (requestId: string) =>
    api<TradeJournalDetail>(`/api/trades/${encodeURIComponent(requestId)}`),

  news: (params: { from?: string; to?: string; source?: string; minRelevance?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.source) qs.set('source', params.source);
    if (params.minRelevance != null) qs.set('minRelevance', String(params.minRelevance));
    const s = qs.toString();
    return api<NewsArticle[]>(`/api/news${s ? `?${s}` : ''}`);
  },
  newsSources: () => api<string[]>('/api/news/sources'),

  reflections: (params: { from?: string; to?: string; agent?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.agent) qs.set('agent', params.agent);
    if (params.q) qs.set('q', params.q);
    const s = qs.toString();
    return api<Reflection[]>(`/api/reflections${s ? `?${s}` : ''}`);
  },
  reflectionAgents: () => api<string[]>('/api/reflections/agents'),

  marketIndices: () => api<MarketIndices>('/api/market/indices'),
};
